/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : Environments.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-07-07
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, DialogContentText, IconButton, Stack } from "@mui/material";
import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { adminSettingsAPI, HandleApiActions } from "../../redux/actions";
import deleteIcon from "../../assets/delete_icon.png";

// Modern UI Components
import CommonBreadcrumbs from "../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import CustomSearchbarWithApiV2 from "../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import CustomDataGridV2 from "../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import * as XLSX from "xlsx";
import PropTypes from "prop-types";

const getEnvironmentList = (resp) => {
    if (Array.isArray(resp?.data)) return resp.data;
    if (Array.isArray(resp?.response?.data)) return resp.response.data;
    return [];
};

function Environments({ open, handleClick }) {
    // Re-render table on tab activation / resize, same as NetworkInterfaces
    const [tableKey, setTableKey] = useState(0);

    useEffect(() => {
        if (open === "environments") {
            setTableKey((prev) => prev + 1);
        }
    }, [open]);

    useEffect(() => {
        const handleResize = () => setTableKey((prev) => prev + 1);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const dispatch = useDispatch();

    const [openAddEnvironment, setOpenAddEnvironment] = useState(false);
    const [openDeleteEnvironment, setOpenDeleteEnvironment] = useState(false);
    const [selectedEnvironment, setSelectedEnvironment] = useState(null);

    const [searchQuery] = useState("");
    const [name, setName] = useState("");

    const [page, setPage] = useState(1);
    const [rowsPerPage] = useState(25);
    const [totalEnvironmentCount, setTotalEnvironmentCount] = useState(0);
    const [environmentList, setEnvironmentList] = useState([]);
    const searchQueryRef = useRef("");
    const filteredRows = useMemo(() => {
        if (!searchQuery) return environmentList || [];
        return (environmentList || []).filter((row) =>
            Object.values(row).some((value) =>
                value?.toString().toLowerCase().includes(searchQuery.toLowerCase()),
            ),
        );
    }, [searchQuery, environmentList]);

    const handleDeleteClick = useCallback((params) => {
        setSelectedEnvironment(params.row);
        setOpenDeleteEnvironment(true);
    }, []);

    // Columns definition
    const COLUMNS = useMemo(
        () => [
            {
                field: "sno",
                headerName: "S.No",
                flex: 0.3,
                minWidth: 60,
                sortable: false,
                renderCell: (params) => {
                    const index = filteredRows.findIndex(
                        (item) => item.id === params.row.id,
                    );
                    if (index === -1) return "";
                    return String((page - 1) * rowsPerPage + index + 1);
                },
            },
            {
                field: "name",
                headerName: "Environment Name",
                flex: 1,
                minWidth: 200,
                sortable: true,
            },
            {
                field: "actions",
                headerName: "Actions",
                flex: 0.4,
                minWidth: 90,
                sortable: false,
                renderCell: (params) => (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                        <IconButton
                            size="small"
                            sx={{ p: 0 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(params);
                            }}
                        >
                            <img
                                src={deleteIcon}
                                alt="delete"
                                style={{ width: 24, height: 24 }}
                            />
                        </IconButton>
                    </Box>
                ),
            },
        ],
        [page, rowsPerPage, filteredRows, handleDeleteClick],
    );

    // API call function with pagination support, same shape as NetworkInterfaces
    const getEnvironmentDetails = useCallback(async () => {
        const resp = await dispatch(
            HandleApiActions({
                ...adminSettingsAPI.get_environment,
                show_toast: false,
                params: {
                    page_number: page,
                    page_size: rowsPerPage,
                },
            }),
        );
        if (resp?.error) {
            setEnvironmentList([]);
            setTotalEnvironmentCount(0);
            return;
        }

        const list = getEnvironmentList(resp);
        setEnvironmentList(list);
        setTotalEnvironmentCount(
            resp?.total_count ?? (Array.isArray(list) ? list.length : 0),
        );
    }, [dispatch, page, rowsPerPage]);

    useEffect(() => {
        getEnvironmentDetails();
    }, [getEnvironmentDetails]);

    useEffect(() => {
        setPage(1);
    }, [searchQuery]);

    const handleExportToExcel = () => {
        if (!filteredRows || filteredRows.length === 0) {
            console.warn("No data to export");
            return;
        }

        const exportData = filteredRows.map((row, index) => ({
            "S.No": index + 1,
            "Environment Name": row.name,
        }));

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(exportData);
        XLSX.utils.book_append_sheet(wb, ws, "Environments");
        XLSX.writeFile(wb, "Environments.xlsx");
    };

    const breadcrumbItems = [
        { label: "Settings" },
        { label: "Feature Portfolio" },
        { label: "Environment" },
    ];

    const handleAddClick = () => {
        setOpenAddEnvironment(true);
    };

    const handleClose = () => {
        setOpenAddEnvironment(false);
        setName("");
    };

    const handleClickAdd = async () => {
        try {
            const reqObj = { name: name.trim() };
            await dispatch(
                HandleApiActions({
                    ...adminSettingsAPI.add_environment,
                    params: reqObj,
                    show_toast: true,
                }),
            );
            setOpenAddEnvironment(false);

            const resp = await dispatch(
                HandleApiActions({
                    ...adminSettingsAPI.get_environment,
                    show_toast: false,
                    params: {
                        page_number: 1,
                        page_size: rowsPerPage,
                    },
                }),
            );
            if (resp?.error) {
                setEnvironmentList([]);
                setTotalEnvironmentCount(0);
            } else {
                const list = getEnvironmentList(resp);
                setEnvironmentList(list);
                setTotalEnvironmentCount(
                    resp?.total_count ?? (Array.isArray(list) ? list.length : 0),
                );
            }
            setPage(1);
            setName("");
        } catch (error) {
            console.error("Error adding environment:", error);
        }
    };

    const handleDeleteEnvironmentConfirm = async () => {
        try {
            const reqObj = { id: selectedEnvironment?.id };
            await dispatch(
                HandleApiActions({
                    ...adminSettingsAPI.delete_environment,
                    params: reqObj,
                    show_toast: true,
                }),
            );
            setOpenDeleteEnvironment(false);

            // If current page becomes empty after delete, step back a page
            const isLastRowOnPage = filteredRows.length === 1 && page > 1;
            await getEnvironmentDetails();
            if (isLastRowOnPage) {
                setPage((prev) => prev - 1);
            }
        } catch (error) {
            console.error("Error deleting environment:", error);
        }
    };

    return (
        <Box
            sx={{
                margin: 0,
                padding: 0,
                width: "100%",
                height: "100%",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#F5F5F5",
            }}
        >
            {/* Breadcrumbs */}
            <Box
                sx={{
                    padding: { xs: "8px 8px 0px 8px", md: "12px 12px 0px 12px" },
                    flexShrink: 0,
                }}
            >
                <CommonBreadcrumbs items={breadcrumbItems} />
            </Box>

            {/* Header with Search and Add Button */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: { xs: "0 16px 12px 16px", md: "0 24px 16px 24px" },
                    flexShrink: 0,
                    mt: 1,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box>
                        <CustomSearchbarWithApiV2
                            params={{
                                page_number: page,
                                page_size: rowsPerPage,
                            }}
                            apiAction={adminSettingsAPI.get_environment}
                            searchKey={"name"}
                            onSearchQueryChange={(query) => {
                                if (query !== searchQueryRef.current) {
                                    searchQueryRef.current = query;
                                    setPage(1);
                                }
                            }}
                            getDataOnSearch={(data, resp) => {
                                if (resp?.error) {
                                    setEnvironmentList([]);
                                    setTotalEnvironmentCount(0);
                                    return;
                                }
                                const searchedList = Array.isArray(data) ? data : [];
                                setEnvironmentList(searchedList);
                                setTotalEnvironmentCount(
                                    resp?.total_count ?? searchedList.length,
                                );
                            }}
                            placeholder={"Search"}
                        />
                    </Box>
                    <Box>
                        <AddButtonV2 handleOnClick={handleAddClick} />
                    </Box>
                </Box>
                <Box>
                    <CustomButtonV2
                        startIcon={<FileDownloadOutlinedIcon />}
                        type="submit"
                        size="small"
                        btnName="Export"
                        handleOnClick={handleExportToExcel}
                    />
                </Box>
            </Box>

            {/* DataGrid Container */}
            <Box
                sx={{
                    height: "76vh",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    padding: { xs: "0 8px", md: "0 16px" },
                    mt: -1,
                }}
                key={`environments-table-${tableKey}`}
            >
                <CustomDataGridV2
                    rows={filteredRows}
                    columns={COLUMNS}
                    isMultiLine={true}
                    showPagination={true}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    handlePageChange={(newPage) => setPage(newPage)}
                    totalItems={totalEnvironmentCount}
                />
            </Box>

            {/* Add Dialog */}
            <CustomDialogBoxV2
                open={openAddEnvironment}
                onClose={handleClose}
                title={"Add Environment"}
                successButtonName={"Add"}
                rejectButtonName={"Close"}
                onClickSuccess={handleClickAdd}
                onDisabled={!name || name.trim().length < 3}
                actionButtonStyle={"continuous"}
                onClickReject={handleClose}
            >
                <Stack spacing={2} sx={{ width: "350px" }}>
                    <CustomFormTextFieldV2
                        label="Environment Name"
                        required={true}
                        value={name}
                        setValue={(val) => {
                            if (val.length <= 35) {
                                setName(val);
                            }
                        }}
                        maxLength={35}
                    />
                </Stack>
            </CustomDialogBoxV2>

            {/* Delete Confirmation Dialog */}
            <CustomDialogBoxV2
                open={openDeleteEnvironment}
                onClose={() => setOpenDeleteEnvironment(false)}
                title={"Delete"}
                successButtonName={"Ok"}
                rejectButtonName={"Cancel"}
                onClickSuccess={handleDeleteEnvironmentConfirm}
                onClickReject={() => setOpenDeleteEnvironment(false)}
                actionButtonStyle={"continuous"}
            >
                <DialogContentText>
                    Are you sure you want to delete?
                </DialogContentText>
            </CustomDialogBoxV2>
        </Box>
    );
}

export default Environments;

Environments.propTypes = {
    open: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    handleClick: PropTypes.func,
};